import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowRight, ArrowSquareOut, PencilSimple } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { ChReferralDrawer } from "@/modules/patients/detail/referral/ch/ch-referral-drawer"
import { chReferralLetterFragment$key } from "@/modules/patients/detail/referral/ch/services/__generated__/chReferralLetterFragment.graphql"
import { chReferralLetterFragment } from "@/modules/patients/detail/referral/ch/services/chReferralLetterFragment.ts"
import { DeReferralDrawer } from "@/modules/patients/detail/referral/de/de-referral-drawer"
import { deReferralLetterFragment$key } from "@/modules/patients/detail/referral/de/services/__generated__/deReferralLetterFragment.graphql"
import { deReferralLetterFragment } from "@/modules/patients/detail/referral/de/services/deReferralLetterFragment"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface Props extends DocumentProps {
  fragmentRef: deReferralLetterFragment$key | chReferralLetterFragment$key
  progressNoteId: string
  isSignMode?: boolean
  onResend?: () => void
  isSelected?: boolean
}

const ReferralLetterDocument = ({
  progressNoteId,
  isEditable,
  fragmentRef,
  onSelect,
  isConclusion,
  isSent,
  isSigned,
  isSignMode,
  onResend,
  isSelected: isSelectedOverride,
}: Props) => {
  const countryCode = useCountryParam()
  const { t } = useTranslation()
  const countryFragment = countryCode === "CH" ? chReferralLetterFragment : deReferralLetterFragment
  const data = useFragment(countryFragment, fragmentRef)
  const setReferralLetterRef = useReferralStoreContext((state) => state.setReferralLetterRef)
  const setProgressNoteId = useReferralStoreContext((state) => state.setProgressNoteId)
  const timeToTreat = useReferralStoreContext((state) => state.timeToTreat)
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const { id } = data ?? {}
  const isSelected = isSelectedOverride ?? (isConclusion && selectedDocumentId === id)

  if (!id) return null

  const tagLabel = isSigned
    ? t(`patient.conclude.${isSignMode ? "signed" : "approved"}`)
    : t(`patient.conclude.${isSignMode ? "signThis" : "approveThis"}`)

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered relative flex items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="referral-letter-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }
        openDrawer(() => (countryCode === "CH" ? <ChReferralDrawer /> : <DeReferralDrawer />), "ReferralDrawer")
        setProgressNoteId(progressNoteId)
        setReferralLetterRef(fragmentRef)
      }}>
      <ArrowRight className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
      <div className="ml-2 min-w-0">
        <ListText className={isSelected ? "text-on-primary-surface" : ""}>
          {t("episodeOfCare.progressNote.referral")}
        </ListText>
        <ListText variant="secondary" className={isSelected ? "text-on-primary-surface" : ""}>
          {timeToTreat &&
            t(`episodeOfCare.progressNote.pointOfCare.timeToTreatOptionList.${timeToTreat.toLowerCase()}`)}
        </ListText>
      </div>
      {isEditable && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="referral-letter-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="absolute right-2 ml-auto hidden items-center gap-1 group-hover:flex">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut className="text-on-surface-subdued" data-testid="referral-letter-view-document-button" />
          </div>
          {onResend && (
            <Button
              variant="tertiary"
              data-testid="referral-letter-resend-button"
              onClick={(e) => {
                e.stopPropagation()
                onResend()
              }}>
              {t("patient.referralLetter.resend")}
            </Button>
          )}
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag className="ml-auto group-hover:hidden" type={isSigned ? "success" : "critical"} label={tagLabel} />
      )}
    </ListItem>
  )
}

export default ReferralLetterDocument
